/**
 * Render a duration window `[startsAt, endsAt)` as `'9:00 AM–9:30 AM'` in the
 * given IANA timezone. 12-hr local clock matches the format used by every
 * other break surface (draft card, banner, decision-option cards, proposal
 * chain) so the tester/admin never mentally translates between two formats.
 */
export function formatBreakWindowInTz(
  startsAt: string,
  endsAt: string,
  timezone: string,
): string {
  return `${formatLocalTime12(startsAt, timezone)}–${formatLocalTime12(endsAt, timezone)}`;
}

/**
 * Render the local-tz calendar date of an instant as `'2026-05-06'`. Used so
 * the panel's date column matches what the tester sees on the schedule grid,
 * even when the UTC date and the local date differ.
 */
export function formatBreakDateInTz(isoInstant: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).formatToParts(new Date(isoInstant));
  const get = (type: 'year' | 'month' | 'day') => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Missing ${type} in formatted date for ${isoInstant}`);
    return part.value;
  };
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Render the local-tz calendar date as `'Apr 4, 2026'` (MMM D, YYYY). The
 * shortened, named form used across the break + time-off request dialogs and
 * their history (#5094): unambiguous (the month is spelled, so no MM/DD vs
 * DD/MM confusion) yet compact enough for the tight info + timeline lines.
 */
export function formatBreakDateMediumInTz(isoInstant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(isoInstant));
}

/**
 * Render a local-clock instant as `'9:00 AM'` / `'1:30 PM'` in the given
 * timezone. Pure formatting — caller passes the UTC instant and the tz; this
 * component never owns timezone state.
 */
export function formatLocalTime12(isoInstant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  }).format(new Date(isoInstant));
}

/**
 * Compact human-friendly day label, projected relative to `nowMs` in the
 * active timezone. Sibling of {@link formatBreakDateInTz} (which emits the
 * ISO `YYYY-MM-DD` form used as a query/key string) — this variant is the
 * *display* projection used as the leading column of break-request rows.
 *
 * Resolution ladder, from most-recognizable to fully-qualified:
 *   - same calendar day as `nowMs`           → `'Today'`
 *   - next calendar day                       → `'Tomorrow'`
 *   - 2–6 days ahead (same calendar week)     → `'Wed'`
 *   - everything else (past, or 7+ days out)  → `'Wed Jun 4'`
 *
 * Past dates always fall through to the full form because weekday-alone is
 * ambiguous once you can't tell future from past at a glance — terminal cards
 * (the only break rows that ever show a past `startsAt`) need the date to
 * disambiguate "Mon May 19 declined" from "Mon May 26 declined".
 *
 * `nowMs` is injected so the renderer can stay pure for tests; in production
 * the caller passes `Date.now()`.
 */
export function formatBreakDayLabelInTz(
  isoInstant: string,
  timezone: string,
  nowMs: number,
): string {
  const targetIso = formatBreakDateInTz(isoInstant, timezone);
  const todayIso = formatBreakDateInTz(new Date(nowMs).toISOString(), timezone);
  const tomorrowIso = formatBreakDateInTz(new Date(nowMs + 24 * 60 * 60 * 1000).toISOString(), timezone);

  if (targetIso === todayIso) return 'Today';
  if (targetIso === tomorrowIso) return 'Tomorrow';

  // YYYY-MM-DD strings sort lexicographically — parse to ms via the UTC anchor
  // so day differences are timezone-stable.
  const todayMs = Date.parse(`${todayIso}T00:00:00Z`);
  const targetMs = Date.parse(`${targetIso}T00:00:00Z`);
  const daysAhead = Math.round((targetMs - todayMs) / (24 * 60 * 60 * 1000));

  if (daysAhead >= 2 && daysAhead <= 6) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone: timezone,
    }).format(new Date(isoInstant));
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  }).formatToParts(new Date(isoInstant));
  const pick = (type: 'weekday' | 'month' | 'day') => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Missing ${type} part for ${isoInstant}`);
    return part.value;
  };
  return `${pick('weekday')} ${pick('month')} ${pick('day')}`;
}

/**
 * Duration of `[startsAt, endsAt)` as `'1.5h'` / `'30m'` / `'45m'` / `'2h'`.
 * Pure math — no timezone projection — used as the trailing chunk of the
 * Row 1 data line on `BreakRequestRow`. Sub-minute resolution is dropped:
 * every window the row surfaces is at least 15-min snapped.
 *
 * Whole-hour windows collapse to `'<n>h'`; sub-hour windows render as
 * `'<n>m'`; mixed windows render as decimal-hour `'<n>.<tenth>h'` when the
 * minutes are 30 (so 1h30m → `1.5h`), otherwise as the `<h>h <m>m` long
 * form. This keeps the common 30-minute and 1-hour breaks ultra-compact.
 */
export function formatBreakDurationLabel(startsAt: string, endsAt: string): string {
  const durationMs = Date.parse(endsAt) - Date.parse(startsAt);
  if (durationMs <= 0) {
    throw new Error(`Break end must be after start (start=${startsAt}, end=${endsAt})`);
  }
  const totalMinutes = Math.floor(durationMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  if (minutes === 30) return `${hours}.5h`;
  return `${hours}h ${minutes}m`;
}

/**
 * Relative-time provenance label — `'just now'` / `'5m ago'` / `'2h ago'` /
 * `'3d ago'` / `'2w ago'`. Used as the trailing chunk of the Row 3 provenance
 * line on terminal break-request rows ("Declined by admin · 2h ago — ..."),
 * so deliberately coarse: the user wants "recent vs older", not seconds.
 *
 * `nowMs` is injected so callers can pin the clock in tests; in production
 * the caller passes `Date.now()`. Past instants return relative labels;
 * future or equal-to-now instants collapse to `'just now'` — provenance
 * rows only ever cite a past decision, so future timestamps are a no-op.
 */
export function formatRelativeAgo(isoInstant: string, nowMs: number): string {
  const thenMs = Date.parse(isoInstant);
  const diffMs = nowMs - thenMs;
  if (diffMs < 60_000) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}
